import {GlobalVariables} from "../GlobalVariables";
import {gsap} from "../LibrariesImport";
import FocusTrap from "../utils/FocusTrap";

export default class HeaderNavs {
	constructor() {
		this.header = document.querySelector(".header-main");

		if (this.header) {
			this.navBg = this.header.querySelector(".nav-main-bg");

			const mobileNavContainer = this.header.querySelector("#header-main-navs");
			this.focusTrap = mobileNavContainer ? new FocusTrap(mobileNavContainer) : null;

			this.toggleMobileNav();
			this.initMegaMenu();
			this.initNavUnderline();
			this.initMobileBackdrop();
			this.initSolutionsHover();

			// Initialisation : masquer le menu au clavier et aux AT s'il démarre fermé sur mobile
			this.updateMobileNavInert();

			document.addEventListener("escape.pressed", () => {
				this.closeAllLev2();
			});

			// Un clic en dehors du nav-main ferme le sous-menu ouvert
			document.addEventListener("click", (e) => {
				if (this.navMain && !this.navMain.contains(e.target)) {
					this.closeAllLev2();
				}
			});

			GlobalVariables.resizeCallbacks.push(this);
		}
	}

	toggleMobileNav() {
		this.navToggle = this.header.querySelector(".nav-toggle");
		this.navToggleText = this.navToggle?.querySelector(".nav-toggle-text");

		if (this.navToggle) {
			this.navToggle.addEventListener("click", () => {
				if (document.body.classList.contains("navs-open")) {
					this.closeMobileNav();
				} else {
					this.openMobileNav();
				}
			});
		}
	}

	initMobileBackdrop() {
		this.navsBackdrop = this.header.querySelector(".navs-backdrop");
		this.navsBackdrop?.addEventListener("click", () => this.closeMobileNav());
	}

	openMobileNav() {
		if (this.mobilePanel) this.mobilePanel.inert = false;
		document.body.classList.add("navs-open");
		this.navToggle?.setAttribute("aria-expanded", "true");
		this.navToggle?.setAttribute("aria-label", this.navToggle.dataset.labelClose);
		if (this.navToggleText) this.navToggleText.textContent = this.navToggleText.dataset.textClose;
		this.focusTrap?.activate(this.navToggle);

		GlobalVariables.lenis?.stop();
	}

	closeMobileNav() {
		document.body.classList.remove("navs-open");
		this.navToggle?.setAttribute("aria-expanded", "false");
		this.navToggle?.setAttribute("aria-label", this.navToggle.dataset.labelOpen);
		if (this.navToggleText) this.navToggleText.textContent = this.navToggleText.dataset.textOpen;
		this.focusTrap?.deactivate();

		const activeMenuItems = this.header.querySelectorAll(".menu-item-lev1.active");
		activeMenuItems.forEach(item => {
			item.classList.remove("active");
			const subnav = item.querySelector(".lev2");
			if (subnav) {
				subnav.classList.remove("show");
			}
		});

		GlobalVariables.lenis?.start();
		this.updateMobileNavInert();
	}

	// Gère inert sur le panneau mobile (.navs) selon le contexte mobile/desktop.
	// Sur mobile, quand le menu est fermé, inert neutralise le panneau entier :
	// - retire les éléments du tab order et de l'arbre AT
	// - empêche le div.navs lui-même de recevoir le focus (overflow:scroll le rend
	//   focusable nativement dans les navigateurs Chromium quand il a du contenu défilable)
	// Sur desktop, inert est retiré car le panneau est visible et intégré au flux.
	updateMobileNavInert() {
		if (!this.mobilePanel) return;
		const isMobile = window.innerWidth <= GlobalVariables.header.getBreakpoint();
		this.mobilePanel.inert = isMobile;
	}

	initMegaMenu() {
		this.mobilePanel = this.header.querySelector(".navs");
		this.navMain = this.header.querySelector(".nav-main");
		this.levelOneItems = this.navMain.querySelectorAll(".menu-item-lev1.menu-item-has-children");

		this.levelOneItems.forEach(levelOne => {
			levelOne.toggle = levelOne.querySelector("button[data-header='toggler']");
			levelOne.levelTwo = levelOne.querySelector(".lev2-items");

			if (!levelOne.levelTwo) {
				return;
			}

			// Sous-menu fermé par défaut : inert le retire du tab order et de l'arbre AT
			// immédiatement, sans attendre la fin de la transition visibility
			levelOne.levelTwo.inert = true;

			levelOne.toggle.addEventListener("click", (e) => {
				e.preventDefault();

				if (levelOne.classList.contains("active")) {
					this.closeLev2(levelOne);
				} else {
					this.openLev2(levelOne);
				}
			});

			if (levelOne.dataset.header === "hover") {
				this.initLev2Hover(levelOne);
			}

			// Tiroir mobile : le bouton "retour" en tête du sous-menu délègue au même
			// bouton toggler pour refermer, puis lui rend le focus.
			levelOne.closer = levelOne.querySelector("[data-header='closer']");
			levelOne.closer?.addEventListener("click", (e) => {
				e.preventDefault();
				levelOne.toggle.click();
				levelOne.toggle.focus();
			});
		});
	}

	openLev2(levelOne) {
		this.closeAllLev2();

		this.header.classList.add("subnav-active");
		this.adjustNavBgHeight(levelOne.levelTwo);
		levelOne.classList.add("active");
		levelOne.levelTwo.inert = false;
		levelOne.levelTwo.classList.add("show");
		levelOne.toggle.setAttribute("aria-expanded", true);

		if (window.innerWidth > GlobalVariables.header.getBreakpoint()) {
			GlobalVariables.lenis?.stop();
		}
	}

	closeLev2(levelOne) {
		this.header.classList.remove("subnav-active");
		this.adjustNavBgHeight(levelOne.levelTwo);
		levelOne.classList.remove("active");
		levelOne.levelTwo.classList.remove("show");
		levelOne.levelTwo.inert = true;
		levelOne.toggle.setAttribute("aria-expanded", false);

		if (window.innerWidth > GlobalVariables.header.getBreakpoint()) {
			GlobalVariables.lenis?.start();
		}
	}

	// Ouverture au survol (desktop, souris uniquement : au tactile le bouton reste le seul
	// déclencheur). Le délai de fermeture laisse traverser l'espace entre l'item et le sous-menu.
	initLev2Hover(levelOne) {
		const isDesktopMouse = (e) => e.pointerType === "mouse" && window.innerWidth > GlobalVariables.header.getBreakpoint();

		levelOne.addEventListener("pointerenter", (e) => {
			if (!isDesktopMouse(e)) return;

			clearTimeout(levelOne.hoverTimeout);
			if (!levelOne.classList.contains("active")) {
				this.openLev2(levelOne);
			}
		});

		levelOne.addEventListener("pointerleave", (e) => {
			if (!isDesktopMouse(e)) return;

			levelOne.hoverTimeout = setTimeout(() => {
				if (levelOne.classList.contains("active")) {
					this.closeLev2(levelOne);
				}
			}, 200);
		});
	}

	// Mega-menu "solutions" : au survol d'une solution, sa pastille + le panneau
	// featured associé s'activent et les autres solutions s'atténuent (cf. _nav-main.scss).
	// Au départ du hover, on retombe sur la solution mise en avant par défaut.
	initSolutionsHover() {
		this.solutionsColumns = this.navMain.querySelector(".lev2-items-solutions-columns");
		this.solutionsFeatured = this.navMain.querySelector(".lev2-items-solutions-featured");

		if (!this.solutionsColumns || !this.solutionsFeatured) {
			return;
		}

		this.solutionItems = this.solutionsColumns.querySelectorAll("[data-solution-item]");
		this.solutionPanels = this.solutionsFeatured.querySelectorAll("[data-solution-panel]");
		this.defaultSolutionId = this.solutionsFeatured.dataset.defaultSolution;

		this.solutionItems.forEach(item => {
			item.addEventListener("mouseenter", () => this.setActiveSolution(item.dataset.solutionItem));
		});

		this.solutionsColumns.addEventListener("mouseleave", () => this.setActiveSolution(null));
	}

	setActiveSolution(id) {
		const activeId = id ?? this.defaultSolutionId;

		this.solutionItems.forEach(item => {
			item.classList.toggle("active", id !== null && item.dataset.solutionItem === id);
		});

		this.solutionPanels.forEach(panel => {
			panel.classList.toggle("active", panel.dataset.solutionPanel === activeId);
		});
	}

	initNavUnderline() {
		this.navPill = this.header.querySelector(".header-nav-pill");
		this.navUnderline = this.header.querySelector(".nav-main-underline");
		this.underlineItems = this.navMain.querySelectorAll(".lev1-items > .menu-item-lev1");

		if (!this.navPill || !this.navUnderline || !this.underlineItems.length) {
			return;
		}

		this.activeNavItem = this.navMain.querySelector(".lev1-items > .menu-item-lev1.current-menu-item, .lev1-items > .menu-item-lev1.current-menu-ancestor, .lev1-items > .menu-item-lev1.current-page-ancestor");

		this.underlineItems.forEach(item => {
			item.addEventListener("mouseenter", () => this.moveNavUnderline(item, true));
		});

		this.navMain.querySelector(".lev1-items").addEventListener("mouseleave", () => {
			if (this.activeNavItem) {
				this.moveNavUnderline(this.activeNavItem, true);
			} else {
				this.hideNavUnderline();
			}
		});

		if (this.activeNavItem) {
			this.moveNavUnderline(this.activeNavItem, false);
		}
	}

	moveNavUnderline(item, animate) {
		const pillRect = this.navPill.getBoundingClientRect();
		const itemRect = item.getBoundingClientRect();
		const x = itemRect.left - pillRect.left + itemRect.width / 2 - this.navUnderline.offsetWidth / 2;

		if (animate && !GlobalVariables.reducedMotion) {
			gsap.to(this.navUnderline, {x, opacity: 1, duration: 0.3, ease: "power2.out"});
		} else {
			gsap.set(this.navUnderline, {x, opacity: 1});
		}
	}

	hideNavUnderline() {
		gsap.to(this.navUnderline, {opacity: 0, duration: 0.3});
	}

	closeAllLev2() {
		this.header.classList.remove("subnav-active");
		this.levelOneItems.forEach(item => {
			item.classList.remove("active");
			item.levelTwo.classList.remove("show");
			item.levelTwo.inert = true;
			item.querySelector("button[data-header='toggler']").setAttribute("aria-expanded", "false");
		});

		if (window.innerWidth > GlobalVariables.header.getBreakpoint()) {
			GlobalVariables.lenis?.start();
		}
	}

	restoreNav() {
		this.closeAllLev2();
		this.closeMobileNav();
	}

	// La croissance visible du panneau vient déjà du `transform: scaleY()` transitionné en CSS
	// (cf. .nav-main-bg / .subnav-active dans _nav-main.scss) : on se contente ici de fixer sa
	// hauteur réelle d'un coup (pas de tween sur `height`, qui forcerait un reflow à chaque tick).
	adjustNavBgHeight(levelTwo) {
		if (this.navBg && window.innerWidth > GlobalVariables.header.getBreakpoint()) {
			gsap.set(this.navBg, { height: levelTwo.offsetHeight });
		}
	}

	resize() {
		this.restoreNav();

		if (this.navUnderline && this.activeNavItem) {
			this.moveNavUnderline(this.activeNavItem, false);
		}
	}
}
